import maplibregl from 'maplibre-gl';
import { useEffect, useMemo } from 'react';

import { useTileConfig } from '@/hooks/useTileConfig';
import addLayer from '@/utils/addLayer';
import removeLayers from '@/utils/removeLayers';

import {
  floodMetaLabelsFillLayer,
  floodMetaLabelsLayer,
} from './floodLablesLayer';
import { floodLayer } from './floodLayer';
import { wetlandsLayer } from './wetlandsLayer';

export const floodImageLegend = {
  fld_0p2_pct: '0.2% annual flood hazard',
  fld_1_pct: '1% annual flood hazard',
  fld_future_1_pct: 'Future 1% annual flood hazard',
  fld_inc_risk: 'Increased risk due to levee',
  fld_red_risk: 'Reduced risk due to levee',
  fld_reg_flood: 'Regulatory Floodway',
  fld_spec_flood: 'Special Floodway',
  fld_undetermined: 'Undetermined flood hazard',
};

const imageMap = Object.keys(floodImageLegend);

type Props = {
  map: maplibregl.Map | undefined;
};

/**
 * This hook handles adding flood data to the map
 */
export const useFlood = ({ map }: Props) => {
  const { tileConfig } = useTileConfig();

  const floodUrl = useMemo(
    () => (tileConfig ? `${tileConfig.flood}` : undefined),
    [tileConfig]
  );

  const floodLayerWithUrl = useMemo(
    () => (floodUrl ? floodLayer(floodUrl) : undefined),
    [floodUrl]
  );

  const wetlandsLayerWithUrl = useMemo(
    () => (floodUrl ? wetlandsLayer(floodUrl) : undefined),
    [floodUrl]
  );

  const floodMetaLabelsLayerWithUrl = useMemo(
    () => (floodUrl ? floodMetaLabelsLayer(floodUrl) : undefined),
    [floodUrl]
  );

  const floodLabelsFillLayerWithUrl = useMemo(
    () => (floodUrl ? floodMetaLabelsFillLayer(floodUrl) : undefined),
    [floodUrl]
  );

  const imageListener = ({ id }: { id: string }) => {
    if (!map) return;

    if (imageMap.indexOf(id) > -1) {
      const img = `/img/flood/${id}.png`;

      map
        .loadImage(img)
        .catch()
        .then((res) => {
          if (map.hasImage(id)) return;

          if (res.data) {
            map.addImage(id, res.data);
          }
        });
    }
  };

  useEffect(() => {
    if (
      !map ||
      !floodLayerWithUrl ||
      !wetlandsLayerWithUrl ||
      !floodMetaLabelsLayerWithUrl ||
      !floodLabelsFillLayerWithUrl
    )
      return () => {};

    map.on('styleimagemissing', imageListener);

    if (floodLayerWithUrl)
      addLayer(map, [
        floodLabelsFillLayerWithUrl,
        floodMetaLabelsLayerWithUrl,
        floodLayerWithUrl,
        wetlandsLayerWithUrl,
      ]);

    return () => {
      map.off('styleimagemissing', imageListener);

      removeLayers(map, [
        floodLayerWithUrl.id,
        wetlandsLayerWithUrl.id,
        floodMetaLabelsLayerWithUrl.id,
        floodLabelsFillLayerWithUrl.id,
      ]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    floodLayerWithUrl,
    wetlandsLayerWithUrl,
    floodMetaLabelsLayerWithUrl,
    floodLabelsFillLayerWithUrl,
  ]);

  return {
    floodLabelsFillLayerWithUrl,
    floodLayerWithUrl,
    floodMetaLabelsLayerWithUrl,
    wetlandsLayerWithUrl,
  };
};
